
import React, { createContext, useContext, ReactNode } from 'react';
import { useConsentManager } from './useConsentManager';
import { ConsentContextType } from './types';
import { ErrorBoundary } from '@/components/ui/error-boundary';

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

const ConsentProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  let consentManager;
  
  try {
    // Try to use the consent manager hook
    consentManager = useConsentManager();
  } catch (error) {
    console.error('Failed to initialize consent manager:', error);
    
    // Fallback: provide safe default values without hooks
    consentManager = {
      consent: {
        essential: true,
        analytics: false,
        marketing: false,
        aiData: false,
      },
      updateConsent: () => {
        console.warn('ConsentManager not available - updateConsent called');
      },
      acceptAll: () => {
        console.warn('ConsentManager not available - acceptAll called');
      },
      rejectNonEssential: () => {
        console.warn('ConsentManager not available - rejectNonEssential called');
      },
      hasUserConsented: () => {
        console.warn('ConsentManager not available - hasUserConsented called');
        return false;
      },
      clearConsent: () => {
        console.warn('ConsentManager not available - clearConsent called');
      },
      isLoading: false,
      saveConsent: () => {
        console.warn('ConsentManager not available - saveConsent called');
      },
    };
  }
  
  const hasConsent = (type: keyof typeof consentManager.consent) => {
    try {
      return consentManager.consent?.[type] || false;
    } catch (error) {
      console.error('Error checking consent:', error);
      return false;
    }
  };

  const contextValue: ConsentContextType = {
    ...consentManager,
    hasConsent
  };

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};

export const ConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary>
      <ConsentProviderInner>
        {children}
      </ConsentProviderInner>
    </ErrorBoundary>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    console.error('useConsent must be used within a ConsentProvider');
    // Return safe defaults instead of throwing
    return {
      consent: {
        essential: true,
        analytics: false,
        marketing: false,
        aiData: false,
      },
      updateConsent: () => {},
      acceptAll: () => {},
      rejectNonEssential: () => {},
      hasUserConsented: () => false,
      clearConsent: () => {},
      isLoading: false,
      saveConsent: () => {},
      hasConsent: () => false,
    };
  }
  return context;
};
